import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOptionChainAction } from '../../../../../store/actions/watchlist';
import octopusInstance from '../../../../../services/octopus/octopusInstance';
import { getExchangeCode } from '../../../../../services/exchanges';
import { initializeDynamicRows } from '../../../../Overview/OptionDataUtils';
import { fetchMarketData } from '../../../../../services/backendAPI/marketDataService';

/**
 * Hook to manage Option Chain data for selected indices.
 * Handles fetching the chain and subscribing to live LTP updates for visible strikes.
 */
export const useOptionChainData = (selectedIndex) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectedExpiry, setSelectedExpiry] = useState(null);
  const [spotPrice, setSpotPrice] = useState(0);
  const [spotChange, setSpotChange] = useState(0);
  const [spotPct, setSpotPct] = useState(0);

  // Get option chain data from Redux
  const rawOptionChain = useSelector(state => state.marketwatch.optionChainData);
  const [filteredChain, setFilteredChain] = useState([]);
  const [availExpiries, setAvailExpiries] = useState([]);

  // Reset state when index changes
  useEffect(() => {
    setSpotPrice(0);
    setSpotChange(0);
    setSpotPct(0);
    setFilteredChain([]);
    setSelectedExpiry(null);
    setAvailExpiries([]);

    // Explicitly clear Global Redux store to avoid "smearing" data from previous index
    dispatch({ type: 'SET_OPTION_CHAIN_DATA', payload: [] });
  }, [selectedIndex, dispatch]);

  // Use a ref for spotPrice to ensure API calls always use the absolutely latest value
  // and can be cleared synchronously on index change to avoid "stale price" payloads.
  const spotPriceRef = useRef(0);
  const fetchTimeoutRef = useRef(null);
  const hasFetchedForCurrentIndex = useRef(false);

  // 2. Fetch Option Chain logic
  const fetchChain = useCallback(() => {
    if (!selectedIndex) return;

    setLoading(true);
    const payload = {
      exchange: selectedIndex.exchange === 'BSE' ? 'BFO' : 'NSE',
      token: selectedIndex.token,
      num: 10,
      // Use the ref specifically to prevent stale values during index switches
      price: spotPriceRef.current || 0,
      instrument_name: selectedIndex.name
    };

    dispatch(fetchOptionChainAction(payload))
      .catch(err => {
        console.error("Option Chain Fetch Failed:", err);
        setFilteredChain([]);
        setAvailExpiries([]);
      })
      .finally(() => setLoading(false));
  }, [selectedIndex, dispatch]);

    // 1. Subscribe to Index Spot Price (Live)
    useEffect(() => {
      if (!selectedIndex) return;
  

    const subscriptionLocation = `index-fno-spot-${selectedIndex.token}`;
    const handler = octopusInstance.wsHandler({
      messageType: 'CompactMarketDataMessage',
      subscriptionLocation,
      payload: {
        exchangeCode: selectedIndex.wsCode || 1,
        instrumentToken: Number(selectedIndex.token),
      },
    });

    handler.subscribe(({ msg }) => {
      const ltp = msg.ltp || msg.lastTradedPrice || 0;
      if (ltp) {
        setSpotPrice(ltp);
        spotPriceRef.current = ltp;

        if (!hasFetchedForCurrentIndex.current) {
          fetchChain();
          hasFetchedForCurrentIndex.current = true;
          if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
        }
      }
      if (msg.absoluteChange !== undefined) setSpotChange(msg.absoluteChange);
      if (msg.percentChange !== undefined) setSpotPct(msg.percentChange);
    });

    return () => {
      handler.unsubscribe();
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };
  }, [selectedIndex, fetchChain]);

  // Trigger fetch exactly once when index changes (with REST fetch & timeout fallback)
  const lastFetchedToken = useRef(null);

  useEffect(() => {
    if (selectedIndex && lastFetchedToken.current !== selectedIndex.token) {
      lastFetchedToken.current = selectedIndex.token;
      hasFetchedForCurrentIndex.current = false;
      spotPriceRef.current = 0;

      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(() => {
        if (!hasFetchedForCurrentIndex.current) {
          fetchChain();
          hasFetchedForCurrentIndex.current = true;
        }
      }, 800); // Fallback timeout if REST API is slow

      let restCancelled = false;
      const exchange = selectedIndex.exchange || 'NSE';
      const token = selectedIndex.token;

      fetchMarketData(exchange, token)
        .then((dataRes) => {
          if (restCancelled) return;
          const data = dataRes?.result || dataRes?.data;
          if (data) {
            const ltp = data.last_trade_price || data.lastTradedPrice || 0;
            if (ltp) {
              setSpotPrice(ltp);
              spotPriceRef.current = ltp;
              if (data.absoluteChange !== undefined) setSpotChange(data.absoluteChange);
              if (data.percentChange !== undefined) setSpotPct(data.percentChange);

              if (!hasFetchedForCurrentIndex.current) {
                fetchChain();
                hasFetchedForCurrentIndex.current = true;
                if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
              }
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch market data for index spot price", err);
        });

      return () => {
        restCancelled = true;
        if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
      };
    }
  }, [selectedIndex, fetchChain]);

  // 3. Process raw data from Redux
  useEffect(() => {
    if (rawOptionChain && rawOptionChain.length > 0) {
      // Helper to parse "16JUN26" to a timestamp for sorting
      const parseExpiry = (dateStr) => {
        if (!dateStr || dateStr.length < 7) return Infinity;
        const day = dateStr.substring(0, 2);
        const month = dateStr.substring(2, 5);
        const year = "20" + dateStr.substring(5, 7);
        const timestamp = new Date(`${day} ${month} ${year}`).getTime();
        return isNaN(timestamp) ? Infinity : timestamp;
      };

      const todayMidnight = new Date().setHours(0,0,0,0);

      // Filter out dummy expiries and PAST expiries
      const validGroups = rawOptionChain.filter(item => {
        if (!item.strikes || item.strikes.length === 0) return false;
        const expiryTime = parseExpiry(item.expiry_date);
        return expiryTime >= todayMidnight;
      });
      
      const uniqueExpiries = [...new Set(validGroups.map(item => item.expiry_date))];
      
      // Sort expiries chronologically so the nearest (latest) is first
      const expiries = uniqueExpiries.sort((a, b) => parseExpiry(a) - parseExpiry(b));

      setAvailExpiries(expiries);

      // Select valid expiry: if none selected OR currently selected is not in the list, default to first
      let currentExp = selectedExpiry;
      if (!currentExp || !expiries.includes(currentExp)) {
        if (expiries.length > 0) {
          currentExp = expiries[0];
          setSelectedExpiry(currentExp);
        }
      }

      // Filter by selected expiry and extract its strikes
      const selectedExpiryGroup = rawOptionChain.find(item => item.expiry_date === currentExp);
      const strikes = selectedExpiryGroup?.strikes || [];

      // Initialize rows with flattened keys (ce_ltp, etc.) for UI
      const processedStrikes = initializeDynamicRows(strikes, selectedIndex.exchange === 'BSE' ? 'BFO' : 'NFO');

      setFilteredChain(prevChain => {
        // If we already have strikes, merge the live LTPs/Changes to prevent API "Zeroing"
        if (prevChain.length === 0) return processedStrikes;

        return processedStrikes.map(newRow => {
          const existing = prevChain.find(p => p.strike_price === newRow.strike_price);
          if (existing) {
            return {
              ...newRow,
              // Preserve WebSocket-driven live values if they are greater than 0
              ce_ltp: (existing.ce_ltp > 0) ? existing.ce_ltp : (newRow.ce_ltp || 0),
              ce_change: (existing.ce_ltp > 0) ? existing.ce_change : (newRow.ce_change || 0),
              ce_abs_change: (existing.ce_ltp > 0) ? existing.ce_abs_change : (newRow.ce_abs_change || 0),

              pe_ltp: (existing.pe_ltp > 0) ? existing.pe_ltp : (newRow.pe_ltp || 0),
              pe_change: (existing.pe_ltp > 0) ? existing.pe_change : (newRow.pe_change || 0),
              pe_abs_change: (existing.pe_ltp > 0) ? existing.pe_abs_change : (newRow.pe_abs_change || 0),

              ce_oi: existing.ce_oi || newRow.ce_oi,
              pe_oi: existing.pe_oi || newRow.pe_oi,
              ce_volume: existing.ce_volume || newRow.ce_volume,
              pe_volume: existing.pe_volume || newRow.pe_volume
            };
          }
          return newRow;
        });
      });
    } else {
      // Explicitly clear data if Redux data is empty (handles API failure/cleanup)
      setFilteredChain([]);
      setAvailExpiries([]);
    }
  }, [rawOptionChain, selectedExpiry, selectedIndex?.exchange]);

  // 4. Live Updates for Option Strikes (LTP)
  useEffect(() => {
    if (filteredChain.length === 0) return;

    // We need to subscribe to both CE and PE instrument tokens in the chain
    const strikeSubscriptions = filteredChain.flatMap(row => [
      { token: row.ce_token, exchange: row.ce_exchange || 'NFO' },
      { token: row.pe_token, exchange: row.pe_exchange || 'NFO' }
    ]).filter(item => item.token);

    const handlers = strikeSubscriptions.map(item => {
      const handler = octopusInstance.wsHandler({
        messageType: 'CompactMarketDataMessage',
        subscriptionLocation: `index-fno-strike-${item.token}`,
        payload: {
          exchangeCode: item.exchange === 'BFO' ? 7 : 2, // NFO is 2
          instrumentToken: Number(item.token),
        },
      });

      handler.subscribe(({ msg }) => {
        setFilteredChain(prev => prev.map(row => {
          if (row.ce_token === item.token) {
            const ltp = msg.ltp || msg.lastTradedPrice || msg.Price || row.ce_ltp || 0;
            const close = row.ce_close || 0;
            const absChange = msg.absoluteChange ?? (close > 0 ? (ltp - close) : (row.ce_abs_change ?? 0));
            const pctChange = msg.percentChange ?? (close > 0 ? ((ltp - close) / close) * 100 : (row.ce_change ?? 0));
            return {
              ...row,
              ce_ltp: ltp,
              ce_change: pctChange,
              ce_abs_change: absChange,
              ce_oi: msg.openInterest || msg.OI || row.ce_oi || 0,
              ce_volume: msg.volume || msg.Volume || row.ce_volume || 0
            };
          }
          if (row.pe_token === item.token) {
            const ltp = msg.ltp || msg.lastTradedPrice || msg.Price || row.pe_ltp || 0;
            const close = row.pe_close || 0;
            const absChange = msg.absoluteChange ?? (close > 0 ? (ltp - close) : (row.pe_abs_change ?? 0));
            const pctChange = msg.percentChange ?? (close > 0 ? ((ltp - close) / close) * 100 : (row.pe_change ?? 0));
            return {
              ...row,
              pe_ltp: ltp,
              pe_change: pctChange,
              pe_abs_change: absChange,
              pe_oi: msg.openInterest || msg.OI || row.pe_oi || 0,
              pe_volume: msg.volume || msg.Volume || row.pe_volume || 0
            };
          }
          return row;
        }));
      });

      return handler;
    });

    return () => handlers.forEach(h => h.unsubscribe());
  }, [JSON.stringify(filteredChain.map(r => `${r.ce_token}-${r.pe_token}`)), selectedExpiry]); // Re-subscribe if tokens actually change

  return {
    spotPrice,
    spotChange,
    spotPct,
    loading,
    optionChain: filteredChain,
    expiries: availExpiries,
    selectedExpiry,
    setSelectedExpiry,
    refresh: fetchChain
  };
};

